/**
 * ═══════════════════════════════════════════════════════════════════════════════
 *                    AUTHORITY AGENT - MOLTBOOK PROTOCOL
 * ═══════════════════════════════════════════════════════════════════════════════
 * 
 * @title AuthorityAgentPage.tsx
 * @description Full Authority AI Agent interface implementing Moltbook protocol
 * @author REDEX Labs / Stratagem Nexus
 * @version 2.0.0
 * @protocol Moltbook Agent-to-Agent
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
  useTheme,
  Avatar,
  Stack,
} from '@mui/material';
import {
  Security,
  VerifiedUser,
  Gavel,
  Add,
  Refresh,
  CheckCircle,
  Warning,
  Schedule,
  ContentCopy,
  Download,
  AccountBalanceWallet,
  Description,
  Lock,
  LockOpen,
  Hub,
  Science,
  Speed,
  Shield,
  Fingerprint,
  Token,
  Policy,
  WorkspacePremium,
  AutoAwesome,
} from '@mui/icons-material';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

interface CaseEntity {
  type: 'wallet' | 'contract' | 'token';
  address: string;
  chain: string;
  label?: string;
}

interface Case {
  id: string;
  title: string;
  status: 'intake' | 'analyzing' | 'pending_proof' | 'verified' | 'closed' | 'disputed';
  createdAt: string;
  entities: CaseEntity[];
  chains: string[];
  executability?: {
    canExecute: boolean;
    blockers: string[];
    requirements: string[];
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════

const AGENT_CONFIG = {
  agentId: 'stratagem-nexus-authority-v2',
  version: '2.0.0',
  capabilities: [
    { name: 'Wallet Derivation Verification', icon: Fingerprint },
    { name: 'Contract Role Analysis', icon: Policy },
    { name: 'Token Approval Check', icon: Token },
    { name: 'Executability Gate', icon: Shield },
    { name: 'Evidence Pack Generation', icon: WorkspacePremium },
    { name: 'On-Chain Attestation', icon: Gavel },
  ],
};

const STATUS_CONFIG: Record<string, { color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'; label: string }> = {
  intake: { color: 'info', label: 'Intake' },
  analyzing: { color: 'warning', label: 'Analyzing' },
  pending_proof: { color: 'secondary', label: 'Pending Proof' },
  verified: { color: 'success', label: 'Verified' },
  closed: { color: 'default', label: 'Closed' },
  disputed: { color: 'error', label: 'Disputed' },
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export default function AuthorityAgentPage() {
  const theme = useTheme();
  const [cases, setCases] = useState<Case[]>([]);
  const [selectedCase, setSelectedCase] = useState<Case | null>(null);
  const [isNewCaseDialogOpen, setIsNewCaseDialogOpen] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  
  // New case form state
  const [newCaseTitle, setNewCaseTitle] = useState('');
  const [newCaseAddress, setNewCaseAddress] = useState('');
  const [newCaseChain, setNewCaseChain] = useState('ethereum');
  const [newCaseEntityType, setNewCaseEntityType] = useState<'wallet' | 'contract' | 'token'>('wallet');

  const handleCreateCase = () => {
    if (!newCaseTitle || !newCaseAddress) return;
    
    const newCase: Case = {
      id: `CASE-${Date.now().toString(36).toUpperCase()}`,
      title: newCaseTitle,
      status: 'intake',
      createdAt: new Date().toISOString(),
      entities: [{
        type: newCaseEntityType,
        address: newCaseAddress,
        chain: newCaseChain,
      }],
      chains: [newCaseChain],
    };
    
    setCases([newCase, ...cases]);
    setSelectedCase(newCase);
    setIsNewCaseDialogOpen(false);
    setNewCaseTitle('');
    setNewCaseAddress('');
  };

  const handleAnalyzeCase = async (caseItem: Case) => {
    setIsAnalyzing(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    const updatedCase: Case = {
      ...caseItem,
      status: 'verified',
      executability: {
        canExecute: true,
        blockers: [],
        requirements: ['Seed phrase access', 'Hardware wallet signature'],
      },
    };
    
    setCases(cases.map(c => c.id === caseItem.id ? updatedCase : c));
    setSelectedCase(updatedCase);
    setIsAnalyzing(false);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: `linear-gradient(135deg, ${alpha(theme.palette.background.default, 0.95)} 0%, ${alpha('#1a1a2e', 0.98)} 100%)`,
      p: 3,
    }}>
      {/* Hero Header */}
      <Paper
        elevation={0}
        sx={{
          background: `linear-gradient(135deg, ${alpha('#7c3aed', 0.15)} 0%, ${alpha('#3b82f6', 0.15)} 50%, ${alpha('#06b6d4', 0.1)} 100%)`,
          border: `1px solid ${alpha('#7c3aed', 0.3)}`,
          borderRadius: 3,
          p: 4,
          mb: 4,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background Pattern */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '40%',
          height: '100%',
          background: `radial-gradient(circle at 80% 50%, ${alpha('#7c3aed', 0.2)} 0%, transparent 50%)`,
          pointerEvents: 'none',
        }} />
        
        <Grid container alignItems="center" spacing={3}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Avatar
                sx={{
                  width: 72,
                  height: 72,
                  background: 'linear-gradient(135deg, #7c3aed 0%, #3b82f6 100%)',
                  boxShadow: `0 8px 32px ${alpha('#7c3aed', 0.4)}`,
                }}
              >
                <Security sx={{ fontSize: 40 }} />
              </Avatar>
              <Box>
                <Typography variant="h3" fontWeight="800" sx={{ 
                  background: 'linear-gradient(90deg, #fff 0%, #a78bfa 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 0.5,
                }}>
                  Authority Agent
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    size="small"
                    icon={<AutoAwesome sx={{ fontSize: 14 }} />}
                    label="Moltbook Protocol v2.0"
                    sx={{
                      bgcolor: alpha('#7c3aed', 0.3),
                      color: '#a78bfa',
                      border: `1px solid ${alpha('#7c3aed', 0.5)}`,
                      '& .MuiChip-icon': { color: '#a78bfa' },
                    }}
                  />
                  <Chip
                    size="small"
                    label={AGENT_CONFIG.agentId}
                    variant="outlined"
                    sx={{ 
                      borderColor: alpha('#fff', 0.2),
                      color: alpha('#fff', 0.7),
                      fontFamily: 'monospace',
                      fontSize: 11,
                    }}
                  />
                </Stack>
              </Box>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<Add />}
              onClick={() => setIsNewCaseDialogOpen(true)}
              sx={{
                background: 'linear-gradient(135deg, #7c3aed 0%, #3b82f6 100%)',
                px: 4,
                py: 1.5,
                fontSize: 16,
                fontWeight: 600,
                boxShadow: `0 8px 32px ${alpha('#7c3aed', 0.4)}`,
                '&:hover': {
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #60a5fa 100%)',
                  boxShadow: `0 12px 40px ${alpha('#7c3aed', 0.5)}`,
                },
              }}
            >
              New Case
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Stats Row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[
          { label: 'Total Cases', value: cases.length, color: '#3b82f6', icon: Description },
          { label: 'Verified', value: cases.filter(c => c.status === 'verified').length, color: '#10b981', icon: VerifiedUser },
          { label: 'Pending', value: cases.filter(c => c.status === 'pending_proof' || c.status === 'analyzing').length, color: '#f59e0b', icon: Schedule },
          { label: 'Attestations', value: 0, color: '#8b5cf6', icon: Gavel },
        ].map((stat, i) => (
          <Grid key={i} size={{ xs: 6, md: 3 }}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                background: alpha(stat.color, 0.1),
                border: `1px solid ${alpha(stat.color, 0.3)}`,
                borderRadius: 2,
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <Box sx={{
                position: 'absolute',
                top: -20,
                right: -20,
                opacity: 0.1,
              }}>
                <stat.icon sx={{ fontSize: 100, color: stat.color }} />
              </Box>
              <Typography variant="overline" sx={{ color: alpha('#fff', 0.6), letterSpacing: 1.5 }}>
                {stat.label}
              </Typography>
              <Typography variant="h2" fontWeight="800" sx={{ color: stat.color }}>
                {stat.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Capabilities Panel */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              background: alpha(theme.palette.background.paper, 0.6),
              backdropFilter: 'blur(10px)',
              border: `1px solid ${alpha('#fff', 0.1)}`,
              borderRadius: 2,
              height: '100%',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <Science sx={{ color: '#7c3aed' }} />
              <Typography variant="h6" fontWeight="700">
                Agent Capabilities
              </Typography>
            </Box>
            
            <Stack spacing={1.5}>
              {AGENT_CONFIG.capabilities.map((cap, i) => (
                <Paper
                  key={i}
                  elevation={0}
                  sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    background: alpha('#10b981', 0.1),
                    border: `1px solid ${alpha('#10b981', 0.2)}`,
                    borderRadius: 1.5,
                  }}
                >
                  <Avatar sx={{ 
                    width: 36, 
                    height: 36, 
                    bgcolor: alpha('#10b981', 0.2),
                  }}>
                    <cap.icon sx={{ fontSize: 18, color: '#10b981' }} />
                  </Avatar>
                  <Typography variant="body2" fontWeight="500">
                    {cap.name}
                  </Typography>
                  <CheckCircle sx={{ ml: 'auto', fontSize: 18, color: '#10b981' }} />
                </Paper>
              ))}
            </Stack>

            <Divider sx={{ my: 3, borderColor: alpha('#fff', 0.1) }} />

            <Alert 
              severity="info" 
              icon={<Shield />}
              sx={{ 
                bgcolor: alpha('#3b82f6', 0.1),
                border: `1px solid ${alpha('#3b82f6', 0.3)}`,
                '& .MuiAlert-icon': { color: '#3b82f6' },
              }}
            >
              <Typography variant="caption">
                All proofs are cryptographically signed and on-chain verifiable.
              </Typography>
            </Alert>
          </Paper>
        </Grid>

        {/* Cases Panel */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              background: alpha(theme.palette.background.paper, 0.6),
              backdropFilter: 'blur(10px)',
              border: `1px solid ${alpha('#fff', 0.1)}`,
              borderRadius: 2,
              minHeight: 400,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6" fontWeight="700">
                {selectedCase ? 'Case Details' : 'Recent Cases'}
              </Typography>
              {selectedCase && (
                <Button size="small" onClick={() => setSelectedCase(null)}>
                  ← Back to List
                </Button>
              )}
            </Box>

            {!selectedCase ? (
              cases.length === 0 ? (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 10,
                  background: alpha('#7c3aed', 0.05),
                  borderRadius: 2,
                  border: `2px dashed ${alpha('#7c3aed', 0.3)}`,
                }}>
                  <Avatar sx={{ 
                    width: 80, 
                    height: 80, 
                    bgcolor: alpha('#7c3aed', 0.1),
                    mx: 'auto',
                    mb: 3,
                  }}>
                    <Description sx={{ fontSize: 40, color: '#7c3aed' }} />
                  </Avatar>
                  <Typography variant="h5" fontWeight="600" sx={{ mb: 1 }}>
                    No Cases Yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Create your first authority verification case to get started
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setIsNewCaseDialogOpen(true)}
                    sx={{
                      background: 'linear-gradient(135deg, #7c3aed 0%, #3b82f6 100%)',
                    }}
                  >
                    Create First Case
                  </Button>
                </Box>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: alpha('#fff', 0.6), fontWeight: 600 }}>Case ID</TableCell>
                        <TableCell sx={{ color: alpha('#fff', 0.6), fontWeight: 600 }}>Title</TableCell>
                        <TableCell sx={{ color: alpha('#fff', 0.6), fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ color: alpha('#fff', 0.6), fontWeight: 600 }}>Created</TableCell>
                        <TableCell align="right" sx={{ color: alpha('#fff', 0.6), fontWeight: 600 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {cases.map((caseItem) => (
                        <TableRow 
                          key={caseItem.id}
                          hover
                          onClick={() => setSelectedCase(caseItem)}
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': { bgcolor: alpha('#7c3aed', 0.1) },
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace" sx={{ color: '#a78bfa' }}>
                              {caseItem.id}
                            </Typography>
                          </TableCell>
                          <TableCell>{caseItem.title}</TableCell>
                          <TableCell>
                            <Chip
                              label={STATUS_CONFIG[caseItem.status]?.label || caseItem.status}
                              color={STATUS_CONFIG[caseItem.status]?.color || 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell sx={{ color: alpha('#fff', 0.6) }}>
                            {new Date(caseItem.createdAt).toLocaleDateString()}
                          </TableCell>
                          <TableCell align="right">
                            <IconButton 
                              size="small" 
                              sx={{ color: '#7c3aed' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedCase(caseItem);
                              }}
                            >
                              <Speed />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )
            ) : (
              <Box>
                {/* Case Header */}
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 3, 
                    mb: 3,
                    background: alpha('#7c3aed', 0.1),
                    border: `1px solid ${alpha('#7c3aed', 0.3)}`,
                    borderRadius: 2,
                  }}
                >
                  <Grid container spacing={2} alignItems="center">
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Chip
                          label={STATUS_CONFIG[selectedCase.status]?.label}
                          color={STATUS_CONFIG[selectedCase.status]?.color}
                        />
                        <Typography variant="h5" fontWeight="600">
                          {selectedCase.title}
                        </Typography>
                      </Stack>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }} sx={{ textAlign: { md: 'right' } }}>
                      <Typography variant="caption" color="text.secondary">Case ID</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', md: 'flex-end' }, gap: 1 }}>
                        <Typography variant="body1" fontFamily="monospace" sx={{ color: '#a78bfa' }}>
                          {selectedCase.id}
                        </Typography>
                        <IconButton size="small" onClick={() => copyToClipboard(selectedCase.id)}>
                          <ContentCopy sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Entities */}
                <Typography variant="subtitle2" sx={{ mb: 2, color: alpha('#fff', 0.6) }}>
                  ENTITIES ({selectedCase.entities.length})
                </Typography>
                {selectedCase.entities.map((entity, i) => (
                  <Paper
                    key={i}
                    elevation={0}
                    sx={{
                      p: 2,
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      background: alpha('#3b82f6', 0.1),
                      border: `1px solid ${alpha('#3b82f6', 0.3)}`,
                      borderRadius: 2,
                    }}
                  >
                    <Avatar sx={{ bgcolor: alpha('#3b82f6', 0.2) }}>
                      <AccountBalanceWallet sx={{ color: '#3b82f6' }} />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Stack direction="row" spacing={1} sx={{ mb: 0.5 }}>
                        <Chip label={entity.type.toUpperCase()} size="small" />
                        <Chip label={entity.chain} size="small" variant="outlined" />
                      </Stack>
                      <Typography variant="body2" fontFamily="monospace" sx={{ color: alpha('#fff', 0.8) }}>
                        {entity.address}
                      </Typography>
                    </Box>
                  </Paper>
                ))}

                {/* Executability Gate */}
                {selectedCase.executability && (
                  <Paper 
                    elevation={0}
                    sx={{ 
                      p: 3, 
                      mb: 3,
                      background: selectedCase.executability.canExecute 
                        ? alpha('#10b981', 0.1) 
                        : alpha('#ef4444', 0.1),
                      border: `1px solid ${selectedCase.executability.canExecute ? alpha('#10b981', 0.3) : alpha('#ef4444', 0.3)}`,
                      borderRadius: 2,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      {selectedCase.executability.canExecute ? (
                        <LockOpen sx={{ fontSize: 32, color: '#10b981' }} />
                      ) : (
                        <Lock sx={{ fontSize: 32, color: '#ef4444' }} />
                      )}
                      <Typography variant="h6" fontWeight="700" sx={{ 
                        color: selectedCase.executability.canExecute ? '#10b981' : '#ef4444' 
                      }}>
                        Executability Gate: {selectedCase.executability.canExecute ? 'CAN EXECUTE' : 'BLOCKED'}
                      </Typography>
                    </Box>
                    {selectedCase.executability.requirements.length > 0 && (
                      <Box sx={{ pl: 6 }}>
                        <Typography variant="caption" color="text.secondary">Requirements:</Typography>
                        {selectedCase.executability.requirements.map((req, i) => (
                          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Warning sx={{ fontSize: 14, color: '#f59e0b' }} />
                            <Typography variant="body2">{req}</Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Paper>
                )}

                {/* Actions */}
                <Stack direction="row" spacing={2}>
                  {selectedCase.status === 'intake' && (
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={isAnalyzing ? <Refresh /> : <Science />}
                      onClick={() => handleAnalyzeCase(selectedCase)}
                      disabled={isAnalyzing}
                      sx={{
                        background: 'linear-gradient(135deg, #7c3aed 0%, #3b82f6 100%)',
                        px: 4,
                      }}
                    >
                      {isAnalyzing ? 'Analyzing...' : 'Analyze & Verify'}
                    </Button>
                  )}
                  {selectedCase.status === 'verified' && (
                    <>
                      <Button
                        variant="contained"
                        size="large"
                        startIcon={<WorkspacePremium />}
                        sx={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}
                      >
                        Generate Evidence Pack
                      </Button>
                      <Button
                        variant="outlined"
                        size="large"
                        startIcon={<Download />}
                      >
                        Export APA
                      </Button>
                    </>
                  )}
                </Stack>

                {isAnalyzing && (
                  <Box sx={{ mt: 3 }}>
                    <LinearProgress sx={{ 
                      height: 8, 
                      borderRadius: 4,
                      bgcolor: alpha('#7c3aed', 0.2),
                      '& .MuiLinearProgress-bar': {
                        background: 'linear-gradient(90deg, #7c3aed 0%, #3b82f6 100%)',
                      }
                    }} />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Running authority verification checks...
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* New Case Dialog */}
      <Dialog 
        open={isNewCaseDialogOpen} 
        onClose={() => setIsNewCaseDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: alpha('#1a1a2e', 0.98),
            border: `1px solid ${alpha('#7c3aed', 0.3)}`,
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: alpha('#7c3aed', 0.2) }}>
              <Security sx={{ color: '#7c3aed' }} />
            </Avatar>
            <Typography variant="h6" fontWeight="600">Create New Case</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="Case Title"
              fullWidth
              value={newCaseTitle}
              onChange={(e) => setNewCaseTitle(e.target.value)}
              placeholder="e.g., Client Wallet Recovery - ETH/BSC"
            />
            
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Entity Type</InputLabel>
                  <Select
                    value={newCaseEntityType}
                    label="Entity Type"
                    onChange={(e) => setNewCaseEntityType(e.target.value as 'wallet' | 'contract' | 'token')}
                  >
                    <MenuItem value="wallet">Wallet</MenuItem>
                    <MenuItem value="contract">Smart Contract</MenuItem>
                    <MenuItem value="token">Token</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Chain</InputLabel>
                  <Select
                    value={newCaseChain}
                    label="Chain"
                    onChange={(e) => setNewCaseChain(e.target.value)}
                  >
                    <MenuItem value="ethereum">Ethereum</MenuItem>
                    <MenuItem value="bsc">BNB Smart Chain</MenuItem>
                    <MenuItem value="polygon">Polygon</MenuItem>
                    <MenuItem value="arbitrum">Arbitrum</MenuItem>
                    <MenuItem value="optimism">Optimism</MenuItem>
                    <MenuItem value="base">Base</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <TextField
              label="Address"
              fullWidth
              value={newCaseAddress}
              onChange={(e) => setNewCaseAddress(e.target.value)}
              placeholder="0x..."
              inputProps={{ style: { fontFamily: 'monospace' } }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={() => setIsNewCaseDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleCreateCase}
            disabled={!newCaseTitle || !newCaseAddress}
            sx={{ background: 'linear-gradient(135deg, #7c3aed 0%, #3b82f6 100%)' }}
          >
            Create Case
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
